<template>
  <div class="sj-datePicker">
    <SjDropdown
      ref="dropdown"
      placement="bottom-start"
      trigger="click"
      @show="init"
      @hide="dropdownShow = false"
      dropdownClass="sj-datePicker-container"
      :closeOnClick="false"
      :arrowVisible="false"
      :appendToBody="appendToBody"
      :boundariesSelector="boundariesSelector"
    >
      <div
        class="sj-datePicker-input"
        slot="reference"
        :class="[`datepicker-size-${size}`]"
      >
        <!-- 禁用状态屏蔽操作的遮罩 -->
        <div class="sj-datePicker-cover" v-if="disabled" @click.stop></div>
        <SjInput
          class="sj-datePicker-input-inner"
          :border="border"
          :size="size"
          :placeholder="placeholder"
          :padding="padding"
          :value="showValue"
          :disabled="disabled"
          :readonly="true"
          cursor="pointer"
          @mouseenter="handleMouseenter"
          @mouseleave="handleMouseleave"
        ></SjInput>
        <i
          :class="[
            'icon_com',
            showClose ? 'commulti_close' : 'comdatepicker_calender',
            'sj-datePicker-icon',
            {
              'sj-datePicker-icon--close': showClose,
              disabled
            }
          ]"
          @mouseenter="handleMouseenter"
          @mouseleave="handleMouseleave"
          @click="handleClear"
        ></i>
      </div>
      <SjCard
        slot="dropdown"
        :class="[
          'sj-datePicker-swapper',
          {
            'sj-datePicker-swapper--range': showSecondPanel
          }
        ]"
      >
        <div
          class="sj-datePicker-shortcuts"
          v-if="shortcut && shortcutList.length"
        >
          <div
            class="sj-datePicker-shortcuts-item"
            v-for="(item, index) in shortcutList"
            :key="index"
            @click="handleShortcutClick(item)"
          >
            {{ item.text }}
          </div>
        </div>
        <div class="sj-datePicker-main">
          <div class="sj-datePicker-head" v-if="type !== 'time'">
            <div class="sj-datePicker-switchContainer">
              <timeSwitch
                v-model="currentDate"
                :enable-input-checker="enableInputChecker"
                :intervalNum="currentSwitchIntervalNum"
                :type="currentSwitchType"
                :showLabel="type !== 'year'"
                :hideNext="showSecondPanel"
                @change="handleCurrentDateChange"
              ></timeSwitch>
            </div>
            <div class="sj-datePicker-switchContainer" v-if="showSecondPanel">
              <timeSwitch
                v-model="currentDate2"
                :enable-input-checker="enableInputChecker"
                :intervalNum="currentSwitchIntervalNum"
                :type="currentSwitchType"
                :showLabel="type !== 'year'"
                hidePre
                @change="handleCurrentDateChange2"
              ></timeSwitch>
            </div>
          </div>
          <div class="sj-datePicker-body">
            <div class="sj-datePicker-panelContainer">
              <component
                v-model="dateValue"
                :is="currentPanel"
                :currentDate="currentDate"
                :range="rangeFinal"
                :multiple="multipleFinal"
                :firstDayOfWeek="firstDayOfWeek"
                :choosing.sync="choosing"
                :chooseStartValue.sync="chooseStartValue"
                :isWeek="type === 'week'"
                @close="handleClose"
                v-if="type !== 'time'"
              ></component>
              <timePanel
                v-else
                v-model="currentTime"
                :dropdownShow="dropdownShow"
                :precision="precision"
              ></timePanel>
            </div>
            <div class="sj-datePicker-panelContainer" v-if="showSecondPanel">
              <component
                v-model="dateValue"
                :is="currentPanel"
                :currentDate="currentDate2"
                :range="rangeFinal"
                :multiple="multipleFinal"
                :firstDayOfWeek="firstDayOfWeek"
                :choosing.sync="choosing"
                :chooseStartValue.sync="chooseStartValue"
                @close="handleClose"
                v-if="type !== 'time'"
              ></component>
              <timePanel
                v-else
                v-model="currentTime2"
                :dropdownShow="dropdownShow"
                :precision="precision"
              ></timePanel>
            </div>
          </div>
          <div class="sj-datePicker-foot" v-if="showFoot">
            <template v-if="type !== 'time' && !multipleFinal">
              <div class="sj-datePicker-foot-datetimeBox">
                <timePopSelector
                  v-model="currentTime"
                  :precision="precision"
                ></timePopSelector>
              </div>
              <div
                class="sj-datePicker-foot-datetimeBox"
                v-if="showSecondPanel"
              >
                <timePopSelector
                  v-model="currentTime2"
                  :precision="precision"
                ></timePopSelector>
              </div>
            </template>
            <div class="sj-datePicker-foot-confirmBox">
              <SjButton
                width="50"
                size="mini"
                :disabled="choosing"
                @click="handleConfirm"
              >
                确定
              </SjButton>
            </div>
          </div>
        </div>
      </SjCard>
    </SjDropdown>
  </div>
</template>

<script>
import moment from "moment";
import sizeMixin from "../../../utils/sizeMixin";
import dateMixin from "./mixin";
import timeSwitch from "./components/timeSwitch";
import timePopSelector from "./components/timePopSelector";
import datePanel from "./panels/date";
import timePanel from "./panels/time";
import monthPanel from "./panels/month";
import quarterPanel from "./panels/quarter";
import halfYearPanel from "./panels/halfYear";
import yearPanel from "./panels/year";

export default {
  name: "SjDatePicker",
  mixins: [sizeMixin, dateMixin],
  components: {
    timeSwitch,
    timePopSelector,
    datePanel,
    timePanel,
    monthPanel,
    quarterPanel,
    halfYearPanel,
    yearPanel
  },
  props: {
    value: [Number, String, Array],
    type: {
      type: String,
      default: "date",
      validator(v) {
        return (
          [
            "date",
            "time",
            "datetime",
            "singleDatetime",
            "week",
            "month",
            "quarter",
            "halfYear",
            "year"
          ].indexOf(v) !== -1
        );
      }
    },
    format: [String, Function],
    valueFormat: String,
    // 范围选择
    range: {
      type: Boolean,
      default: false
    },
    rangeSeparator: {
      type: String,
      default: "-"
    },
    // 多选
    multiple: {
      type: Boolean,
      default: false
    },
    multipleSeparator: {
      type: String,
      default: ";"
    },
    firstDayOfWeek: {
      type: Number,
      default: 0
    },
    border: {
      type: Boolean,
      default: true
    },
    padding: {
      type: Number,
      default: 10
    },
    placeholder: {
      type: String,
      default: "请选择"
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    shortcut: {
      type: Boolean,
      default: false
    },
    shortcutOptions: {
      type: Array,
      default: () => []
    },
    precision: {
      type: String,
      default: "second",
      validator(v) {
        return ["second", "minute", "hour"].indexOf(v) !== -1;
      }
    },
    defaultTime: {
      type: Array,
      default() {
        return ["00:00:00"];
      }
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    boundariesSelector: {
      type: String,
      default: "body"
    },
    enableInputChecker: {
      type: Boolean,
      default: false
    },
    validator: Function
  },
  data() {
    return {
      typeConfig: {
        date: {
          format: "YYYY-MM-DD",
          valueFormat: "YYYY-MM-DD",
          interval: "month"
        },
        time: {
          format: "HH:mm:ss",
          valueFormat: "HH:mm:ss",
          interval: false
        },
        datetime: {
          format: "YYYY-MM-DD HH:mm:ss",
          valueFormat: "YYYY-MM-DD HH:mm:ss",
          interval: "month"
        },
        singleDatetime: {
          format: "YYYY-MM-DD HH:mm:ss",
          valueFormat: "YYYY-MM-DD HH:mm:ss",
          interval: "month"
        },
        week: {
          format: "YYYY年第w周",
          valueFormat: "YYYY-MM-DD",
          interval: "month"
        },
        month: {
          format: "YYYY-MM",
          valueFormat: "YYYY-MM",
          interval: "year"
        },
        quarter: {
          format: "YYYY年第Q季度",
          valueFormat: "YYYY年第Q季度",
          interval: "year"
        },
        halfYear: {
          format: "halfYear",
          valueFormat: "YYYY-MM",
          interval: "year"
        },
        year: {
          format: "YYYY",
          valueFormat: "YYYY",
          intervalNum: 20,
          interval: "year"
        }
      },
      dropdownShow: false,
      isHovering: false,
      dateValue: null,
      valueNum: null,
      // 是否正在选择区域
      choosing: false,
      chooseStartValue: null,
      currentDate: null,
      currentDate2: null,
      timeFormat: "HH:mm:ss",
      // 时刻值
      currentTime: null,
      currentTime2: null
    };
  },
  computed: {
    showSecondPanel() {
      const { type, range } = this;
      if (type === "week") {
        return false;
      }
      return range;
    },
    rangeFinal() {
      const { type, range } = this;
      if (type === "week" || type === "singleDatetime") {
        return false;
      }
      return range;
    },
    valueIsArr() {
      return this.rangeFinal || (this.type === "singleDatetime" && this.range);
    },
    multipleFinal() {
      const { multiple, type } = this;
      const excludeTypes = ["time", "datetime", "singleDatetime"];
      return multiple && !excludeTypes.includes(type);
    },
    currentPanel() {
      const { type } = this;
      if (type !== "datetime" && type !== "week" && type !== "singleDatetime") {
        return `${type}Panel`;
      }
      return "datePanel";
    },
    currentSwitchType() {
      const { type, typeConfig } = this;
      return typeConfig[type].interval;
    },
    currentSwitchIntervalNum() {
      const { type, typeConfig } = this;
      return typeConfig[type].intervalNum;
    },
    currentFormat() {
      const { format, type, typeConfig } = this;
      return format || typeConfig[type].format;
    },
    currentValueFormat() {
      const { valueFormat, type, typeConfig } = this;
      return valueFormat || typeConfig[type].valueFormat;
    },
    showFoot() {
      const { type, multipleFinal } = this;
      return (
        type === "time" ||
        type === "datetime" ||
        type === "singleDatetime" ||
        multipleFinal
      );
    },
    showValue() {
      const {
        value,
        valueIsArr,
        rangeSeparator,
        multipleFinal,
        multipleSeparator,
        formatConversion,
        currentValueFormat,
        currentFormat
      } = this;
      if (!valueIsArr) {
        if (multipleFinal && Array.isArray(value)) {
          return value
            .map(item =>
              formatConversion(item, currentValueFormat, currentFormat)
            )
            .join(multipleSeparator);
        }
        return formatConversion(value, currentValueFormat, currentFormat);
      }
      if (
        Object.prototype.toString.call(value) === "[object Array]" &&
        value.length >= 2
      ) {
        return `${formatConversion(
          value[0],
          currentValueFormat,
          currentFormat
        )} ${rangeSeparator} ${formatConversion(
          value[1],
          currentValueFormat,
          currentFormat
        )}`;
      }
      return "";
    },
    showClose() {
      const { isHovering, showValue, clearable, disabled } = this;
      if (
        clearable &&
        !disabled &&
        showValue !== "" &&
        showValue !== null &&
        isHovering
      ) {
        return true;
      }
      return false;
    },
    shortcutList() {
      const { shortcutOptions } = this;
      return shortcutOptions;
    }
  },
  methods: {
    init() {
      this.dropdownShow = true;
      this.choosing = false;
      this.chooseStartValue = null;
      this.initDateValue();
    },
    initDateValue() {
      const {
        value,
        type,
        valueIsArr,
        multipleFinal,
        getDateTime,
        currentValueFormat
      } = this;
      if (valueIsArr) {
        if (Array.isArray(value) && value[0] && value[1]) {
          const valueNum1 = getDateTime(value[0], currentValueFormat);
          const valueNum2 = getDateTime(value[1], currentValueFormat);
          if (type === "singleDatetime") {
            this.dateValue = valueNum1;
          } else {
            this.dateValue = [valueNum1, valueNum2];
          }
          this.valueNum = [valueNum1, valueNum2];
        } else {
          if (type === "singleDatetime") {
            this.dateValue = null;
          } else {
            this.dateValue = [];
          }
          this.valueNum = [];
        }
      } else {
        if (multipleFinal && Array.isArray(value)) {
          this.dateValue = value.map(item =>
            getDateTime(item, currentValueFormat)
          );
        } else {
          this.dateValue = getDateTime(value, currentValueFormat);
        }
        this.valueNum = this.dateValue;
      }
      this.initCurrentDate();
      this.initCurrentTime();
    },
    initCurrentDate() {
      const {
        valueNum,
        showSecondPanel,
        type,
        valueIsArr,
        multipleFinal,
        typeConfig
      } = this;
      if (
        valueNum === null ||
        ((valueIsArr || multipleFinal) && valueNum && !valueNum.length)
      ) {
        this.currentDate = Date.now();
      } else {
        if (valueIsArr || multipleFinal) {
          this.currentDate = valueNum[0];
        } else {
          this.currentDate = valueNum;
        }
      }

      if (showSecondPanel) {
        this.currentDate2 = this.getNextDate(
          this.currentDate,
          typeConfig[type].interval,
          typeConfig[type].intervalNum
        );
      }
    },
    initCurrentTime() {
      const { valueNum, valueIsArr, multipleFinal } = this;
      if (
        valueNum === null ||
        ((valueIsArr || multipleFinal) && valueNum && !valueNum.length)
      ) {
        this.currentTime = this.getTimeValue(0);
        if (valueIsArr) {
          this.currentTime2 = this.getTimeValue(1);
        }
      } else {
        if (valueIsArr) {
          this.currentTime = this.getTimeValue(0, valueNum[0]);
          this.currentTime2 = this.getTimeValue(1, valueNum[1]);
        } else {
          this.currentTime = this.getTimeValue(
            0,
            multipleFinal ? valueNum[0] : valueNum
          );
        }
      }
    },
    // 获取时刻值组
    getTimeValue(index, dateTime) {
      if (typeof dateTime === "number") {
        const momentDate = moment(dateTime);
        return [momentDate.hour(), momentDate.minute(), momentDate.second()];
      }
      const { defaultTime, timeFormat } = this;
      if (
        Object.prototype.toString.call(defaultTime) === "[object Array]" &&
        defaultTime.length
      ) {
        const timeStr =
          index >= defaultTime.length ? defaultTime[0] : defaultTime[index];
        if (typeof timeStr === "string") {
          const momentTime = moment(timeStr, timeFormat);
          if (momentTime.isValid()) {
            return [
              momentTime.hour(),
              momentTime.minute(),
              momentTime.second()
            ];
          }
        }
      }
      return [0, 0, 0];
    },
    handleCurrentDateChange(value) {
      if (this.showSecondPanel) {
        this.currentDate2 = this.getNextDate(
          value,
          this.typeConfig[this.type].interval,
          this.typeConfig[this.type].intervalNum
        );
      }
    },
    handleCurrentDateChange2(value) {
      this.currentDate = this.getPreDate(
        value,
        this.typeConfig[this.type].interval,
        this.typeConfig[this.type].intervalNum
      );
    },
    handleMouseenter() {
      this.isHovering = true;
    },
    handleMouseleave() {
      this.isHovering = false;
    },
    handleClear(e) {
      const { showClose, valueIsArr, multipleFinal } = this;
      if (showClose) {
        e.stopPropagation();
        const value = valueIsArr || multipleFinal ? [] : null;
        this.$emit("input", value);
        this.$emit("change", value);
        this.$refs.dropdown.doClose();
      }
    },
    handleConfirm() {
      if (!this.choosing) {
        this.emitClose(true);
      }
    },
    // 快捷方式选择
    handleShortcutClick(item) {
      const { type } = this;
      if (type === "time") {
        this.valueNum = item.onClick();
        this.initCurrentTime();
      } else {
        this.dateValue = item.onClick();
      }
      this.emitClose(false);
    },
    // 获取最终的时间值
    getDateValueFinal(mergeTime = false) {
      let { dateValue } = this;
      const {
        type,
        valueIsArr,
        multipleFinal,
        getFormatValue,
        currentValueFormat
      } = this;
      if (dateValue !== null || type === "time") {
        if (!valueIsArr) {
          if (multipleFinal && Array.isArray(dateValue)) {
            return dateValue.map(item =>
              getFormatValue(
                item,
                currentValueFormat,
                mergeTime,
                this.currentTime
              )
            );
          }
          return getFormatValue(
            type === "time" ? 0 : dateValue,
            currentValueFormat,
            mergeTime,
            this.currentTime
          );
        }
        if (type === "time") {
          dateValue = [0, 0];
        }
        if (
          (type === "singleDatetime" && typeof dateValue === "number") ||
          (dateValue.length &&
            typeof dateValue[0] === "number" &&
            typeof dateValue[1] === "number")
        ) {
          const finalValue1 = getFormatValue(
            type === "singleDatetime" ? dateValue : dateValue[0],
            currentValueFormat,
            mergeTime,
            this.currentTime
          );
          const finalValue2 = getFormatValue(
            type === "singleDatetime" ? dateValue : dateValue[1],
            currentValueFormat,
            mergeTime,
            this.currentTime2
          );
          const { getDateTime } = this;
          if (
            getDateTime(finalValue1, currentValueFormat) >
            getDateTime(finalValue2, currentValueFormat)
          ) {
            return [finalValue2, finalValue1];
          }
          return [finalValue1, finalValue2];
        }
        return [];
      }
      if (valueIsArr || multipleFinal) {
        return [];
      }
      return null;
    },
    // 获取时间戳
    getDateTime(value, format) {
      if (typeof value === "number") {
        return value;
      }
      if (typeof value === "string") {
        const momentDate = moment(value, format);
        // if (this.type === "week") {
        //   momentDate.add(this.firstDayOfWeek, "days");
        // }
        return momentDate.toDate().getTime();
      }
      return null;
    },
    // 根据时间戳 格式化时间
    getFormatValue(
      value,
      format,
      mergeTime = false,
      timeValue = [0, 0, 0],
      isValue = true
    ) {
      if (typeof value === "number") {
        const momentDate = moment(value);
        if (mergeTime) {
          momentDate
            .hour(timeValue[0])
            .minute(timeValue[1])
            .second(timeValue[2]);
        }
        if (typeof format === "function") {
          return format(momentDate.toDate().getTime());
        } else if (format === "timestamp") {
          return momentDate.toDate().getTime();
        } else if (format === "halfYear") {
          return `${momentDate.format("YYYY年")}${
            momentDate.month() ? "下半年" : "上半年"
          }`;
        } else if (this.type === "week" && !isValue) {
          return momentDate
            .subtract(this.firstDayOfWeek, "days")
            .add(6, "days")
            .format(format);
        }
        return momentDate.format(format);
      }
      return null;
    },
    // 格式转换
    formatConversion(value, format_from, format_to) {
      return this.getFormatValue(
        this.getDateTime(value, format_from),
        format_to,
        false,
        [0, 0, 0],
        false
      );
    },
    handleClose() {
      if (this.type !== "datetime" && this.type !== "singleDatetime") {
        this.emitClose(true);
      }
    },
    emitClose(mergeTime) {
      const newValue = this.getDateValueFinal(mergeTime);
      const { validator } = this;
      if (validator && !validator(newValue)) {
        return;
      }
      this.$emit("input", newValue);
      this.$emit("change", newValue);
      this.$refs.dropdown.doClose();
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler() {
        this.initDateValue();
      }
    }
  }
};
</script>

<style lang="less">
.sj-datePicker-container {
  border: 0 !important;
}
</style>

<style lang="less" scoped>
@datePicker-width: 280px;
@datePicker-foot-height: 30px;

.sj-datePicker {
  display: inline-block;

  .sj-datePicker-input {
    position: relative;
    .sj-datePicker-cover {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: 2;
      cursor: not-allowed;
    }
    .sj-datePicker-input-inner {
      padding-right: 34px !important;
      .text-ellipsis();

      &:hover {
        border-color: @color-highlight;
      }
    }
    .sj-datePicker-icon {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      display: inline-block;
      cursor: pointer;
      color: @color-normal;

      &.sj-datePicker-icon--close {
        font-size: 12px!important;
        right: 10px;
        &:hover {
          color: @color-available;
        }
      }

      &.disabled {
        color: @color-light;
      }
    }

    each(@sizes, {
      &.datepicker-size-@{value} {
        @size: "@{value}-size";
        .sj-datePicker-icon {
          .icon_size(@value);
        }
      }
    });
  }


}

.sj-datePicker-swapper {
  position: relative;
  display: flex;
  align-items: stretch;
  &.sj-datePicker-swapper--range {
    .sj-datePicker-main {
      width: @datePicker-width * 2;
    }
  }

  .sj-datePicker-shortcuts {
    padding: 10px;
    box-sizing: border-box;
    min-width: 60px;
    border-right: 1px solid @color-gray-3;
    text-align: left;
    overflow-x: hidden;
    overflow-y: auto;

    .sj-datePicker-shortcuts-item {
      white-space: nowrap;
      .text("caption", "normal");
      margin-bottom: 10px;
      cursor: pointer;

      &:last-child {
        margin-bottom: 0;
      }

      &:hover {
        color: @color-highlight;
      }
    }
  }

  .sj-datePicker-main {
    width: @datePicker-width;
    .sj-datePicker-head {
      border-bottom: 1px solid @color-gray-3;

      .sj-datePicker-switchContainer {
        width: @datePicker-width;
        display: inline-block;
      }
    }

    .sj-datePicker-body {
      .sj-datePicker-panelContainer {
        width: @datePicker-width;
        box-sizing: border-box;
        padding: 5px;
        display: inline-block;
        vertical-align: top;
      }
    }

    .sj-datePicker-foot {
      position: relative;
      border-top: 1px solid @color-gray-3;
      height: @datePicker-foot-height;

      .sj-datePicker-foot-datetimeBox {
        width: @datePicker-width;
        height: 100%;
        display: inline-flex;
        justify-content: center;
        align-items: center;
      }

      .sj-datePicker-foot-confirmBox {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
}
</style>
